<template>
  <main>
    <div class="row">
      <div class="col-12">
        <div class="tile">
          <div class="form-inline">
            <b-form-input class="mr-1 mb-1" v-model="filterFormModel.keyword" v-on:keydown.enter="load"
                          :placeholder="$t('keyword')"></b-form-input>
            <b-form-select class="mr-1 mb-1" v-model="filterFormModel.status" @change="load">
              <option :value="undefined">{{ $t('all') }}</option>
              <option value="0">{{ $t('partnerDomain.enabled') }}</option>
              <option value="1">{{ $t('partnerDomain.disabled') }}</option>
            </b-form-select>
            <div class="mr-1 mb-1 pd-agency-filter" style="min-width:230px" v-if="canEditMeta">
              <admin-user-select :model="filterFormModel" attribute="agency" roleFilter="agency"
                                 :place-holder="$t('partnerDomain.agencyFilter')"></admin-user-select>
            </div>
            <button @click="load" class="btn btn-primary mr-1 mb-1">{{ $t('search') }}</button>
            <button @click="reset" class="btn btn-secondary mr-1 mb-1">Reset</button>
          </div>
        </div>
      </div>
      <div class="col-12">
        <div class="c-sorting" v-if="canCreate">
          <div class="row">
            <div class="col-lg-12 c-sorting__right">
              <div class="c-sorting__btn">
                <button @click="create" class="btn btn-sm btn-success">
                  <font-awesome-icon icon="plus"/>&nbsp;{{ $t('create') }}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div class="c-paging clearfix">
          <div class="c-paging__count" v-if="itemCount">{{ $t('found', [itemCount]) }}</div>
          <div class="c-paging__right">
            <div class="c-paging__numbers">
              <b-pagination v-if="itemCount > filterFormModel.pageSize" class="pull-right" size="sm"
                            v-model="filterFormModel.page" :total-rows="itemCount"
                            :per-page="filterFormModel.pageSize" @input="load"></b-pagination>
            </div>
          </div>
        </div>
      </div>
      <div class="col-12" v-if="items.length">
        <div class="c-smart-table tile">
          <div class="row c-smart-table__title">
            <div class="col-lg-3 text-left">{{ $t('partnerDomain.colDomain') }}</div>
            <div class="col-lg-2 text-left">{{ $t('partnerDomain.agency') }}</div>
            <div class="col-lg-1 text-center">{{ $t('partnerDomain.clientCount') }}</div>
            <div class="col-lg-2 text-center">{{ $t('partnerDomain.colTheme') }}</div>
            <div class="col-lg-2 text-center">{{ $t('status') }}</div>
            <div class="col-lg-2"></div>
          </div>
          <div class="row align-items-center pd-row" v-for="item in items" :key="item._id">
            <!-- Logo + tên + domain -->
            <div class="col-lg-3 d-flex align-items-center">
              <img v-if="item.branding && item.branding.logo" :src="item.branding.logo" alt="logo"
                   style="height:34px;width:auto;max-width:60px;object-fit:contain;margin-right:10px"/>
              <font-awesome-icon v-else icon="globe" class="text-muted mr-2" size="lg"/>
              <div>
                <div><b>{{ item.name }}</b></div>
                <a :href="'https://' + item.domain" target="_blank" class="small">{{ item.domain }}</a>
              </div>
            </div>
            <!-- Tài khoản đối tác -->
            <div class="col-lg-2">
              <span v-if="item.agency"><font-awesome-icon icon="user-tie" class="text-info"/> {{ item.agency.name }}</span>
              <span v-else class="text-danger small"><font-awesome-icon icon="exclamation-triangle"/> {{ $t('partnerDomain.noAgency') }}</span>
            </div>
            <!-- Số khách hàng -->
            <div class="col-lg-1 text-center">
              <span class="badge badge-info" style="font-size:13px;padding:5px 9px">
                <font-awesome-icon icon="users"/> {{ item.clientCount || 0 }}
              </span>
            </div>
            <!-- Preview giao diện: title + dải màu ngang -->
            <div class="col-lg-2 text-center">
              <div v-if="item.branding && item.branding.title" class="small text-secondary text-truncate" :title="item.branding.title">{{ item.branding.title }}</div>
              <div class="d-flex justify-content-center" style="gap:4px;flex-wrap:wrap;margin-top:3px">
                <span v-for="c in activeColors(item)" :key="c" :title="item.branding[c]"
                      :style="{ width:'16px', height:'16px', borderRadius:'3px', border:'1px solid #ccc', background:item.branding[c] }"></span>
                <span v-if="!activeColors(item).length" class="small text-muted">—</span>
              </div>
            </div>
            <!-- Trạng thái -->
            <div class="col-lg-2 text-center">
              <span class="badge" :class="item.enabled ? 'badge-success' : 'badge-secondary'">
                {{ item.enabled ? $t('partnerDomain.enabled') : $t('partnerDomain.disabled') }}
              </span>
            </div>
            <!-- Hành động -->
            <div class="col-lg-2 d-flex justify-content-end align-items-center flex-nowrap" v-if="canWrite">
              <router-link :to="{ path: '/client', query: { partnerDomain: item._id } }"
                           class="btn btn-sm btn-outline-primary is-border-1" :title="$t('partnerDomain.viewClients')">
                <font-awesome-icon icon="users"/>
              </router-link>
              <button @click="update(item._id)" type="button" class="btn btn-sm btn-info ml-1" :title="$t('edit')">
                <font-awesome-icon icon="edit"/>
              </button>
              <button @click="del(item._id)" type="button" v-if="canDelete" class="btn btn-sm btn-danger ml-1" :title="$t('delete')">
                <font-awesome-icon icon="trash"/>
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="col-12 text-center text-danger" v-if="!items.length && !listLoading">
        <span>{{ $t('notFoundSearch') }}</span>
      </div>
    </div>

    <b-modal v-model="showForm" size="lg" :title="formTitle" @ok="onSave" no-close-on-backdrop no-enforce-focus>
      <div class="pd-form">

        <!-- KHU VỰC 1: Thông tin chung -->
        <div class="pd-section">
          <div class="pd-section__title"><font-awesome-icon icon="globe"/> {{ $t('partnerDomain.secGeneral') }}</div>
          <!-- owner/admin: sửa đầy đủ field meta -->
          <template v-if="canEditMeta">
            <form-input :label="$t('partnerDomain.domainLabel')" :model="formModel" attribute="domain"
                        :errors="formErrors" placeholder="crm.doitac.com" :required="true"></form-input>
            <form-input :label="$t('partnerDomain.name')" :model="formModel" attribute="name"
                        :errors="formErrors" :required="true"></form-input>
            <admin-user-select :model="formModel" attribute="agency" roleFilter="agency"
                               :label="$t('partnerDomain.agency')" :required="true" :place-holder="$t('partnerDomain.chooseAgency')"
                               :errors="formErrors"></admin-user-select>
            <b-form-group :label="$t('partnerDomain.otpClient')" :description="$t('partnerDomain.otpClientDesc')">
              <client-select :model="formModel" attribute="client" :place-holder="$t('partnerDomain.otpClientPh')"></client-select>
            </b-form-group>
            <form-checkbox :chk-label="$t('partnerDomain.enabledCheckbox')" :model="formModel" attribute="enabled"
                           :errors="formErrors"></form-checkbox>
          </template>
          <!-- agency: chỉ xem domain/tên, KHÔNG sửa -->
          <template v-else>
            <b-form-group :label="$t('partnerDomain.domainLabel')">
              <a :href="'https://' + formModel.domain" target="_blank"><b>{{ formModel.domain }}</b></a>
            </b-form-group>
            <b-form-group :label="$t('partnerDomain.name')">
              <div>{{ formModel.name }}</div>
            </b-form-group>
            <p class="small text-muted mb-0"><font-awesome-icon icon="info-circle"/> {{ $t('partnerDomain.agencyReadonlyNote') }}</p>
          </template>
        </div>

        <!-- KHU VỰC 2: Nhận diện thương hiệu (logo/favicon/tiêu đề) -->
        <div class="pd-section">
          <div class="pd-section__title"><font-awesome-icon icon="image"/> {{ $t('partnerDomain.secBrand') }}</div>
          <form-input :label="$t('partnerDomain.titleField')" :model="formModel.branding" attribute="title"
                      :errors="formErrors"></form-input>
          <b-form-group :label="'Logo'" :description="$t('partnerDomain.logoHint')">
            <div class="d-flex align-items-center">
              <img v-if="formModel.branding.logo" :src="formModel.branding.logo" alt="logo"
                   style="max-height:40px;margin-right:8px"/>
              <b-form-file accept="image/png,image/jpeg,image/jpg" :placeholder="$t('partnerDomain.choosePngJpg')"
                           @input="file => uploadAsset(file, 'logo')"></b-form-file>
              <button v-if="formModel.branding.logo" type="button" class="btn btn-sm btn-link text-danger"
                      @click="formModel.branding.logo = ''"><font-awesome-icon icon="trash-alt" :title="$t('partnerDomain.clear')"/></button>
            </div>
          </b-form-group>
          <b-form-group :label="'Favicon'" :description="$t('partnerDomain.faviconHint')">
            <div class="d-flex align-items-center">
              <img v-if="formModel.branding.favicon" :src="formModel.branding.favicon" alt="favicon"
                   style="max-height:24px;margin-right:8px"/>
              <b-form-file accept="image/png,image/jpeg,image/jpg" :placeholder="$t('partnerDomain.choosePngJpg')"
                           @input="file => uploadAsset(file, 'favicon')"></b-form-file>
              <button v-if="formModel.branding.favicon" type="button" class="btn btn-sm btn-link text-danger"
                      @click="formModel.branding.favicon = ''"><font-awesome-icon icon="trash-alt" :title="$t('partnerDomain.clear')"/></button>
            </div>
          </b-form-group>
        </div>

        <!-- KHU VỰC 3: Trang đăng nhập (banner + slogan) -->
        <div class="pd-section">
          <div class="pd-section__title"><font-awesome-icon icon="sign-in-alt"/> {{ $t('partnerDomain.secLogin') }}</div>
          <b-form-group :label="$t('partnerDomain.loginBanner')" :description="$t('partnerDomain.loginBannerDesc')">
            <div class="d-flex align-items-center">
              <img v-if="formModel.branding.loginBanner" :src="formModel.branding.loginBanner" alt="banner"
                   style="max-height:60px;margin-right:8px"/>
              <b-form-file accept="image/png,image/jpeg,image/jpg" :placeholder="$t('partnerDomain.choosePngJpg')"
                           @input="file => uploadAsset(file, 'loginBanner', 3)"></b-form-file>
              <button v-if="formModel.branding.loginBanner" type="button" class="btn btn-sm btn-link text-danger"
                      @click="formModel.branding.loginBanner = ''"><font-awesome-icon icon="trash-alt" :title="$t('partnerDomain.clear')"/></button>
            </div>
          </b-form-group>
          <b-form-group :label="$t('partnerDomain.loginSlogan')" :description="$t('partnerDomain.loginSloganDesc')">
            <b-form-textarea v-model="formModel.branding.loginSlogan" rows="2" max-rows="4"
                             :placeholder="sloganPlaceholder"></b-form-textarea>
          </b-form-group>
        </div>

        <!-- KHU VỰC 4: Màu sắc giao diện dashboard -->
        <div class="pd-section">
          <div class="pd-section__title"><font-awesome-icon icon="palette"/> {{ $t('partnerDomain.secColors') }}</div>
          <p class="small text-muted">{{ $t('partnerDomain.colorsHint') }}</p>
          <div class="row">
            <div class="col-md-6" v-for="c in colorFields" :key="c">
              <b-form-group :label="$t('partnerDomain.' + c)">
                <div class="d-flex align-items-center">
                  <input type="color" :value="formModel.branding[c] || '#000000'"
                         @input="e => formModel.branding[c] = e.target.value" style="width:42px;height:32px;margin-right:8px"/>
                  <b-form-input v-model="formModel.branding[c]" placeholder="#RRGGBB" style="max-width:120px"></b-form-input>
                  <button v-if="formModel.branding[c]" type="button" class="btn btn-sm btn-link text-danger"
                          @click="formModel.branding[c] = ''"><font-awesome-icon icon="trash-alt" :title="$t('partnerDomain.clear')"/></button>
                </div>
              </b-form-group>
            </div>
          </div>
        </div>

        <!-- KHU VỰC 5: Liên kết & liên hệ (owner/admin) — RỖNG = ẨN link trên domain đối tác -->
        <div class="pd-section" v-if="canEditConfig">
          <div class="pd-section__title"><font-awesome-icon icon="link"/> {{ $t('partnerDomain.secLinks') }}</div>
          <p class="small text-muted">{{ $t('partnerDomain.linksDesc') }}</p>
          <div class="row">
            <div class="col-md-6"><form-input :label="$t('partnerDomain.lTerms')" :model="formModel.links" attribute="terms" :errors="formErrors"></form-input></div>
            <div class="col-md-6"><form-input :label="$t('partnerDomain.lPrivacy')" :model="formModel.links" attribute="privacy" :errors="formErrors"></form-input></div>
            <div class="col-md-6"><form-input :label="$t('partnerDomain.lGuide')" :model="formModel.links" attribute="guide" :errors="formErrors"></form-input></div>
            <div class="col-md-6"><form-input :label="$t('partnerDomain.lSupport')" :model="formModel.links" attribute="support" :errors="formErrors"></form-input></div>
            <div class="col-md-6"><form-input :label="$t('partnerDomain.lWebsite')" :model="formModel.links" attribute="website" :errors="formErrors"></form-input></div>
            <div class="col-md-6"><form-input :label="$t('partnerDomain.lHotline')" :model="formModel.links" attribute="hotline" :errors="formErrors"></form-input></div>
            <div class="col-md-6"><form-input :label="$t('partnerDomain.lContactEmail')" :model="formModel.links" attribute="contactEmail" :errors="formErrors"></form-input></div>
            <div class="col-md-6"><form-input :label="$t('partnerDomain.lPricing')" :model="formModel.links" attribute="pricing" :errors="formErrors" placeholder="https://doitac.com/bang-gia"></form-input></div>
            <div class="col-md-6"><form-input :label="$t('partnerDomain.appName')" :model="formModel" attribute="appName" :errors="formErrors"></form-input></div>
          </div>
        </div>

        <!-- KHU VỰC 6: Email (owner/admin) -->
        <div class="pd-section" v-if="canEditConfig">
          <div class="pd-section__title"><font-awesome-icon icon="envelope"/> {{ $t('partnerDomain.secEmail') }}</div>
          <p class="small text-muted">{{ $t('partnerDomain.emailDesc') }}</p>
          <div class="row">
            <div class="col-md-6"><form-input :label="$t('partnerDomain.eFromName')" :model="formModel.email" attribute="fromName" :errors="formErrors"></form-input></div>
            <div class="col-md-6"><form-input :label="$t('partnerDomain.eLogoUrl')" :model="formModel.email" attribute="logoUrl" :errors="formErrors"></form-input></div>
            <div class="col-md-6"><form-input :label="$t('partnerDomain.eCompanyName')" :model="formModel.email" attribute="companyName" :errors="formErrors"></form-input></div>
            <div class="col-md-6"><form-input :label="$t('partnerDomain.eCompanyAddress')" :model="formModel.email" attribute="companyAddress" :errors="formErrors"></form-input></div>
          </div>
          <b-form-group :label="$t('partnerDomain.eFooter')">
            <b-form-textarea v-model="formModel.email.footerHtml" rows="2" max-rows="6" :placeholder="$t('partnerDomain.eFooterPh')"></b-form-textarea>
          </b-form-group>
          <!-- Cách gửi email hệ thống (3 mode): callio mặc định / Sender Signature / SMTP đối tác -->
          <div class="pd-section__sub mt-2">
            <div class="small font-weight-bold mb-2"><font-awesome-icon icon="paper-plane"/> {{ $t('partnerDomain.sendMode') }}</div>
            <b-form-radio-group v-model="formModel.email.mode" stacked>
              <b-form-radio value="callio">{{ $t('partnerDomain.modeCallio') }}</b-form-radio>
              <b-form-radio value="signature">{{ $t('partnerDomain.modeSignature') }}</b-form-radio>
              <b-form-radio value="smtp">{{ $t('partnerDomain.modeSmtp') }}</b-form-radio>
            </b-form-radio-group>

            <!-- mode=callio -->
            <div class="alert alert-light border small mt-2 mb-0" v-if="emailMode === 'callio'">{{ $t('partnerDomain.modeCallioHint') }}</div>

            <!-- mode=signature: Sender Signature (Postmark xác nhận địa chỉ đối tác) -->
            <div class="mt-3" v-if="emailMode === 'signature'">
              <div class="small font-weight-bold mb-1">
                {{ $t('partnerDomain.senderTitle') }}
                <span class="badge ml-1" :class="senderBadgeClass" v-if="formModel.id">{{ senderStatusLabel }}</span>
              </div>
              <div class="row">
                <div class="col-md-8"><form-input :label="$t('partnerDomain.eFromAddress')" :model="formModel.email" attribute="fromAddress" :errors="formErrors"></form-input></div>
              </div>
              <div class="alert alert-light border small mb-2" style="white-space:pre-line">{{ $t('partnerDomain.senderGuide') }}</div>
              <template v-if="formModel.id">
                <button type="button" class="btn btn-sm btn-primary mr-1 mb-1" :disabled="senderActing" @click="senderConfirm">
                  <font-awesome-icon icon="paper-plane"/> {{ $t('partnerDomain.senderSend') }}
                </button>
                <button type="button" class="btn btn-sm btn-outline-primary is-border-1 mr-1 mb-1" :disabled="senderActing" @click="senderCheck">
                  <font-awesome-icon icon="sync"/> {{ $t('partnerDomain.senderCheck') }}
                </button>
                <button type="button" class="btn btn-sm btn-outline-secondary is-border-1 mb-1" :disabled="senderActing || senderStatus === 'none'" @click="senderResend">
                  {{ $t('partnerDomain.senderResend') }}
                </button>
              </template>
              <div class="small text-muted" v-else>{{ $t('partnerDomain.senderSaveFirst') }}</div>
            </div>

            <!-- mode=smtp: gửi qua mail server riêng của đối tác -->
            <div class="mt-3" v-if="emailMode === 'smtp'">
              <div class="small font-weight-bold mb-1">{{ $t('partnerDomain.smtpTitle') }}</div>
              <div class="row">
                <div class="col-md-6">
                  <b-form-group :label="$t('partnerDomain.smtpHost')">
                    <b-form-input v-model="formModel.email.smtp.host" placeholder="smtp.congty.vn"></b-form-input>
                  </b-form-group>
                </div>
                <div class="col-md-3">
                  <b-form-group :label="$t('partnerDomain.smtpPort')">
                    <b-form-input type="number" v-model.number="formModel.email.smtp.port" placeholder="587"></b-form-input>
                  </b-form-group>
                </div>
                <div class="col-md-3">
                  <b-form-group :label="$t('partnerDomain.smtpSecure')">
                    <b-form-checkbox v-model="formModel.email.smtp.secure" switch class="mt-2">SSL (465)</b-form-checkbox>
                  </b-form-group>
                </div>
                <div class="col-md-6">
                  <b-form-group :label="$t('partnerDomain.smtpUser')">
                    <b-form-input v-model="formModel.email.smtp.user" placeholder="info@congty.vn"></b-form-input>
                  </b-form-group>
                </div>
                <div class="col-md-6">
                  <b-form-group :label="$t('partnerDomain.smtpPass')">
                    <b-form-input type="password" autocomplete="new-password" v-model="formModel.email.smtp.pass"
                                  :placeholder="formModel.email.smtp.hasPass ? $t('partnerDomain.smtpPassSet') : ''"></b-form-input>
                  </b-form-group>
                </div>
              </div>
              <div class="alert alert-light border small mb-2" style="white-space:pre-line">{{ $t('partnerDomain.smtpGuide') }}</div>
              <template v-if="formModel.id">
                <div class="d-flex align-items-center flex-wrap">
                  <b-form-input v-model="smtpTestTo" :placeholder="$t('partnerDomain.smtpTestTo')" style="max-width:260px" class="mr-2 mb-1"></b-form-input>
                  <button type="button" class="btn btn-sm btn-primary mb-1" :disabled="smtpTesting" @click="smtpTest">
                    <font-awesome-icon icon="paper-plane"/> {{ $t('partnerDomain.smtpTest') }}
                  </button>
                </div>
                <div class="small mt-1" :class="smtpTestOk ? 'text-success' : 'text-danger'" v-if="smtpTestMsg">{{ smtpTestMsg }}</div>
              </template>
              <div class="small text-muted" v-else>{{ $t('partnerDomain.senderSaveFirst') }}</div>
            </div>
          </div>
        </div>

        <!-- KHU VỰC 7: Livechat (owner/admin) — code nhúng đầy đủ; rỗng = không có livechat -->
        <div class="pd-section" v-if="canEditConfig">
          <div class="pd-section__title"><font-awesome-icon icon="comments"/> {{ $t('partnerDomain.secLivechat') }}</div>
          <b-form-group :label="$t('partnerDomain.livechatCode')" :description="$t('partnerDomain.livechatDesc')">
            <b-form-textarea v-model="formModel.livechat.embedCode" rows="3" max-rows="12" class="text-monospace"
                             :placeholder="$t('partnerDomain.livechatPh')"></b-form-textarea>
          </b-form-group>
          <b-form-group :label="$t('partnerDomain.livechatAppUrl')" :description="$t('partnerDomain.livechatAppUrlDesc')">
            <b-form-input v-model="formModel.livechat.appUrl" placeholder="https://..."></b-form-input>
          </b-form-group>
        </div>

        <!-- KHU VỰC 9: App mobile (owner/admin) — link store đối tác; rỗng = ẩn popup tải app -->
        <div class="pd-section" v-if="canEditConfig">
          <div class="pd-section__title"><font-awesome-icon icon="mobile-alt"/> {{ $t('partnerDomain.secApp') }}</div>
          <p class="small text-muted">{{ $t('partnerDomain.appDesc') }}</p>
          <div class="row">
            <div class="col-md-6"><form-input :label="$t('partnerDomain.appIos')" :model="formModel.app" attribute="ios" :errors="formErrors"></form-input></div>
            <div class="col-md-6"><form-input :label="$t('partnerDomain.appAndroid')" :model="formModel.app" attribute="android" :errors="formErrors"></form-input></div>
            <div class="col-md-6"><form-input :label="$t('partnerDomain.appIosVersion')" :model="formModel.app" attribute="iosVersion" :errors="formErrors" placeholder="1.0.0"></form-input></div>
            <div class="col-md-6"><form-input :label="$t('partnerDomain.appAndroidVersion')" :model="formModel.app" attribute="androidVersion" :errors="formErrors" placeholder="1.0.0"></form-input></div>
          </div>
        </div>

        <!-- KHU VỰC 8: Chia sẻ khách cho Callio (owner/admin) -->
        <div class="pd-section" v-if="canEditMeta">
          <div class="pd-section__title"><font-awesome-icon icon="user-shield"/> {{ $t('partnerDomain.secShare') }}</div>
          <form-checkbox :chk-label="$t('partnerDomain.shareLabel')" :model="formModel" attribute="shareCustomersWithCallio" :errors="formErrors"></form-checkbox>
          <p class="small text-muted mb-0">{{ $t('partnerDomain.shareDesc') }}</p>
        </div>

        <!-- KHU VỰC 10: Thanh toán đối tác (owner/admin) — tiền về TK đối tác; Billing + QR ăn theo -->
        <div class="pd-section" v-if="canEditConfig">
          <div class="pd-section__title"><font-awesome-icon icon="credit-card"/> {{ $t('partnerDomain.secPayment') }}</div>
          <form-checkbox :chk-label="$t('partnerDomain.paymentEnabled')" :model="formModel.payment" attribute="enabled" :errors="formErrors"></form-checkbox>
          <p class="small text-muted">{{ $t('partnerDomain.paymentDesc') }}</p>
          <div class="row" v-if="formModel.payment.enabled">
            <div class="col-md-6">
              <b-form-group :label="$t('partnerDomain.paymentBank')">
                <b-form-select v-model="formModel.payment.bankCode" @change="onBankChange">
                  <option value="">— {{ $t('partnerDomain.paymentChooseBank') }} —</option>
                  <option v-for="b in banks" :key="b.code" :value="b.code">{{ b.name }}</option>
                </b-form-select>
              </b-form-group>
            </div>
            <div class="col-md-6"><form-input :label="$t('partnerDomain.paymentHolder')" :model="formModel.payment" attribute="accountHolder" :errors="formErrors"></form-input></div>
            <div class="col-md-6"><form-input :label="$t('partnerDomain.paymentNumber')" :model="formModel.payment" attribute="accountNumber" :errors="formErrors"></form-input></div>
          </div>
        </div>

      </div>
    </b-modal>
  </main>
</template>

<script>
import List from '../../mixins/List';
import CRUD from "../../mixins/CRUD";
import AdminUserSelect from "@/components/AdminUserSelect";
import ClientSelect from "@/components/ClientSelect";

function emptyBranding() {
  return { logo: '', favicon: '', primaryColor: '', secondaryColor: '', sidebarBgColor: '', headerBgColor: '', title: '', loginBanner: '', loginSlogan: '' };
}
function emptyLinks() {
  return { terms: '', privacy: '', guide: '', support: '', website: '', hotline: '', contactEmail: '', pricing: '' };
}
function emptyEmail() {
  return {
    fromName: '', fromAddress: '', logoUrl: '', footerHtml: '', companyName: '', companyAddress: '',
    senderStatus: 'none', senderSignatureId: null,
    mode: 'callio',
    smtp: { host: '', port: 587, secure: false, user: '', pass: '', hasPass: false }
  };
}
function emptyApp() {
  return { ios: '', android: '', iosVersion: '', androidVersion: '' };
}
function emptyPayment() {
  return { enabled: false, bankCode: '', bankName: '', accountHolder: '', accountNumber: '' };
}

export default {
  components: { AdminUserSelect, ClientSelect },
  mixins: [List, CRUD],
  data: function () {
    return {
      service: '/partner-domain',
      autoLoad: true,
      senderActing: false,
      smtpTesting: false,
      smtpTestTo: '',
      smtpTestOk: false,
      smtpTestMsg: '',
      sloganPlaceholder: 'Phần mềm quản lý kinh doanh tập trung\nThúc đẩy tăng trưởng thật',
      colorFields: ['primaryColor', 'secondaryColor', 'sidebarBgColor', 'headerBgColor'],
      // Mã ngân hàng VietQR (img.vietqr.io chấp nhận short code) — danh sách NH phổ biến VN
      banks: [
        { code: 'VCB', name: 'Vietcombank' }, { code: 'MB', name: 'TMCP Quân đội (MBBank)' },
        { code: 'TCB', name: 'Techcombank' }, { code: 'BIDV', name: 'BIDV' },
        { code: 'ICB', name: 'VietinBank' }, { code: 'ACB', name: 'ACB' },
        { code: 'VPB', name: 'VPBank' }, { code: 'TPB', name: 'TPBank' },
        { code: 'STB', name: 'Sacombank' }, { code: 'VIB', name: 'VIB' },
        { code: 'SHB', name: 'SHB' }, { code: 'HDB', name: 'HDBank' },
        { code: 'MSB', name: 'MSB' }, { code: 'OCB', name: 'OCB' },
        { code: 'SCB', name: 'SCB' }, { code: 'VBA', name: 'Agribank' },
        { code: 'EIB', name: 'Eximbank' }, { code: 'SeAB', name: 'SeABank' },
        { code: 'NAB', name: 'Nam A Bank' }, { code: 'BAB', name: 'Bac A Bank' },
        { code: 'LPB', name: 'LPBank' }, { code: 'PVCB', name: 'PVcomBank' },
        { code: 'CAKE', name: 'CAKE by VPBank' }, { code: 'VRB', name: 'VRB' }
      ],
      defaultFormModel: {
        domain: null,
        name: null,
        agency: null,
        client: null,
        enabled: true,
        appName: '',
        shareCustomersWithCallio: true,
        branding: emptyBranding(),
        links: emptyLinks(),
        email: emptyEmail(),
        livechat: { embedCode: '', appUrl: '' },
        app: emptyApp(),
        payment: emptyPayment()
      }
    }
  },
  computed: {
    // agency chỉ sửa branding domain của mình → KHÔNG tạo/xoá, KHÔNG sửa field meta.
    isAgency() {
      return this.$store.state.user.role === 'agency';
    },
    canEditMeta() {
      // Chỉ owner/admin: sửa Thông tin chung (domain/name/agency) + Chia sẻ khách
      return ['owner', 'admin'].includes(this.$store.state.user.role);
    },
    canEditConfig() {
      // owner/admin + agency + tech: sửa thông tin domain (branding/links/email/livechat/app/payment)
      return ['owner', 'admin', 'agency', 'tech'].includes(this.$store.state.user.role);
    },
    emailMode() {
      return (this.formModel.email && this.formModel.email.mode) || 'callio';
    },
    senderStatus() {
      return (this.formModel.email && this.formModel.email.senderStatus) || 'none';
    },
    senderStatusLabel() {
      return { none: 'Chưa xác nhận', pending: 'Chờ xác nhận', confirmed: 'Đã xác nhận' }[this.senderStatus] || this.senderStatus;
    },
    senderBadgeClass() {
      return { none: 'badge-secondary', pending: 'badge-warning', confirmed: 'badge-success' }[this.senderStatus] || 'badge-secondary';
    },
    canCreate() {
      return ['owner', 'admin'].includes(this.$store.state.user.role);
    },
    canWrite() {
      return ['owner', 'admin', 'agency', 'tech'].includes(this.$store.state.user.role);
    },
    canDelete() {
      return ['owner'].includes(this.$store.state.user.role);
    }
  },
  i18n: {
    messages: {
      en: {
        all: 'All', status: 'Status',
        partnerDomain: {
          title: 'Partner domains', domain: 'Domain', domainLabel: 'Domain (FQDN)', name: 'Partner name',
          titleField: 'Display title', colors: 'Colors', enabled: 'Active', disabled: 'Inactive',
          enabledCheckbox: 'Active', clear: 'Remove', choosePngJpg: 'PNG/JPG only',
          logoHint: 'PNG/JPG, transparent background. Best ~200×60px, ≤ 2MB.',
          faviconHint: 'Square PNG/JPG, best 32×32 or 64×64px, ≤ 2MB.',
          colorsHint: 'Main overall colors of the CRM.',
          viewClients: 'Partner customers', agencyFilter: 'Filter by partner account',
          secGeneral: 'General', secBrand: 'Brand identity', secLogin: 'Login page (white-label)', secColors: 'Dashboard colors',
          agency: 'Partner account', chooseAgency: 'Choose partner account', noAgency: 'No partner account',
          otpClient: 'Billing account for OTP (SMS/ZNS)', otpClientPh: 'Select the partner Callio account',
          otpClientDesc: 'The partner Callio account holding the approved SMS brandname / Zalo OA / OTP templates. Registration OTP on this domain is sent via that account and billed to it (allowed to go negative). Empty / not configured → OTP is NOT sent (no Callio fallback).',
          agencyRequired: 'Please choose a partner account (required)',
          agencyReadonlyNote: 'Domain & partner account are managed by Callio. You can edit branding and the domain settings below — not this section.',
          colDomain: 'Partner / Domain', colTheme: 'Theme', clientCount: 'Customers',
          loginBanner: 'Login banner image', loginBannerDesc: 'Right-side image on login/forgot/register (PNG/JPG, ≤3MB)',
          loginSlogan: 'Login slogan', loginSloganDesc: 'Description shown under the banner. Each line is one row.',
          primaryColor: 'Primary color', secondaryColor: 'Secondary color',
          sidebarBgColor: 'Sidebar background', headerBgColor: 'Header background',
          secLinks: 'Links & contact', linksDesc: 'Empty field → link hidden on partner domain (no Callio fallback).',
          lTerms: 'Terms of service URL', lPrivacy: 'Privacy policy URL', lGuide: 'User guide URL', lSupport: 'Support URL',
          lWebsite: 'Website URL', lHotline: 'Hotline', lContactEmail: 'Contact email', lPricing: 'Service pricing URL', appName: 'App name (PWA)',
          secEmail: 'Email branding', emailDesc: 'Branding for system/notification emails sent to the partner\'s customers (password reset, OTP, invoices...): sender name, logo, footer, company name/address. The sending address stays a Callio-verified address for inbox deliverability.',
          eFromName: 'From name', eFromAddress: 'Your sender email', eLogoUrl: 'Email logo URL', eCompanyName: 'Company name',
          eCompanyAddress: 'Company address', eFooter: 'Email footer (HTML)', eFooterPh: 'HTML footer for emails...',
          senderTitle: 'Send from your own address (Sender Signature)',
          senderGuide: 'Send system emails FROM the partner address for real:\n1. Enter the sender email above (CUSTOM DOMAIN, e.g. support@yourcompany.com — NOT Gmail/Yahoo/Hotmail, Postmark rejects those) → Save.\n2. Click "Send confirmation":\n  • If the domain is already verified → status turns "Confirmed" INSTANTLY, nothing else to do.\n  • If it is a new domain → Postmark emails that address; open it, click the confirmation link, then click "Check".\n(Empty / not confirmed → still sent from the Callio address, no interruption.)',
          senderSend: 'Send confirmation', senderCheck: 'Check', senderResend: 'Resend confirmation',
          senderSaveFirst: 'Save the partner domain first to manage the sender address.',
          sendMode: 'How to send system emails',
          modeCallio: 'Callio address (default) — sent from a Callio-verified address with partner branding. No setup.',
          modeSignature: 'Partner address via Postmark (Sender Signature) — needs a custom domain + DNS.',
          modeSmtp: 'Partner SMTP server — send through the partner\'s own mail server.',
          modeCallioHint: 'Emails are sent from the Callio address but carry the partner branding (logo/footer/sender name). Best deliverability, zero setup.',
          smtpTitle: 'Partner SMTP server',
          smtpHost: 'SMTP host', smtpPort: 'Port', smtpSecure: 'Encryption', smtpUser: 'Username (email)', smtpPass: 'Password',
          smtpPassSet: '•••••• (saved — leave blank to keep)',
          smtpGuide: 'Enter the partner mail server info → Save → click "Send test".\nPort 465 = tick SSL; port 587/25 = leave SSL unticked (STARTTLS/plain).\nIf SMTP fails when sending, the system falls back to the Callio address so security emails are never lost.',
          smtpTest: 'Send test', smtpTestTo: 'Test recipient (default: you)',
          smtpTestSent: 'Test email sent to {0}. Check the inbox.',
          secLivechat: 'Livechat', livechatCode: 'Embed code (web)', livechatDesc: 'Embed the full chat widget (HTML/JS). If left empty, this domain will have NO livechat.',
          livechatAppUrl: 'Livechat link (mobile app)', livechatAppUrlDesc: 'URL the mobile app opens for support chat. Empty → no livechat in the app.',
          livechatPh: '<scr' + 'ipt>...</scr' + 'ipt>',
          secShare: 'Customer data sharing', shareLabel: 'Share customers with Callio',
          shareDesc: 'Unchecked: Callio sales cannot see customers of this domain and they are not pushed to Callio internal CRM.',
          secApp: 'Mobile app', appDesc: 'Partner app store links (their white-label app). If left empty, the "get the app" prompt is hidden on this domain.',
          appIos: 'App Store URL (iOS)', appAndroid: 'Google Play URL (Android)',
          appIosVersion: 'Latest iOS version', appAndroidVersion: 'Latest Android version',
          secPayment: 'Payment', paymentEnabled: 'Use partner\'s own bank account',
          paymentDesc: 'When enabled, the payment page shows the partner bank account + auto-generates a VietQR. Money goes to the partner — Callio does NOT auto-reconcile or top up credit.',
          paymentBank: 'Bank', paymentChooseBank: 'Choose bank', paymentHolder: 'Account holder', paymentNumber: 'Account number'
        }
      },
      vi: {
        all: 'Tất cả', status: 'Trạng thái',
        partnerDomain: {
          title: 'Domain đối tác', domain: 'Domain', domainLabel: 'Domain (FQDN)', name: 'Tên đối tác',
          titleField: 'Tiêu đề hiển thị', colors: 'Màu sắc', enabled: 'Đang hoạt động', disabled: 'Ngừng hoạt động',
          enabledCheckbox: 'Được hoạt động', clear: 'Xoá', choosePngJpg: 'Chỉ PNG/JPG',
          logoHint: 'PNG/JPG, nền trong suốt. Kích thước tốt nhất ~200×60px, dung lượng ≤ 2MB.',
          faviconHint: 'PNG/JPG vuông, tốt nhất 32×32 hoặc 64×64px, dung lượng ≤ 2MB.',
          colorsHint: 'Màu sắc tổng quan chính của CRM.',
          viewClients: 'Khách hàng của đối tác', agencyFilter: 'Lọc theo tài khoản đối tác',
          secGeneral: 'Thông tin chung', secBrand: 'Nhận diện thương hiệu', secLogin: 'Trang đăng nhập (white-label)', secColors: 'Màu sắc dashboard',
          agency: 'Tài khoản đối tác', chooseAgency: 'Chọn tài khoản đối tác', noAgency: 'Chưa gắn tài khoản',
          otpClient: 'Tài khoản ghi nhận OTP (SMS/ZNS)', otpClientPh: 'Chọn tài khoản Callio của đại lý',
          otpClientDesc: 'Tài khoản Callio của đại lý — nơi chứa SMS brandname / Zalo OA / template OTP đã duyệt. OTP đăng ký trên domain này gửi qua tài khoản đó và trừ cước vào đó (cho trừ âm). Để trống / chưa cấu hình → KHÔNG gửi OTP (không fallback Callio).',
          agencyRequired: 'Vui lòng chọn Tài khoản đối tác (bắt buộc)',
          agencyReadonlyNote: 'Domain & tài khoản đối tác do Callio quản lý. Bạn chỉnh được giao diện + các thông tin domain bên dưới — riêng phần này không sửa được.',
          colDomain: 'Đối tác / Domain', colTheme: 'Giao diện', clientCount: 'Khách hàng',
          loginBanner: 'Ảnh banner trang đăng nhập', loginBannerDesc: 'Ảnh cột phải trang login/forgot/register (PNG/JPG, ≤3MB)',
          loginSlogan: 'Câu giới thiệu', loginSloganDesc: 'Mô tả hiển thị dưới banner. Mỗi dòng là một hàng.',
          primaryColor: 'Màu chính', secondaryColor: 'Màu phụ',
          sidebarBgColor: 'Nền sidebar', headerBgColor: 'Nền header',
          secLinks: 'Liên kết & liên hệ', linksDesc: 'Field rỗng → ẩn link trên domain đối tác (KHÔNG hiện link Callio).',
          lTerms: 'Link Điều khoản dịch vụ', lPrivacy: 'Link Chính sách dữ liệu cá nhân', lGuide: 'Link Hướng dẫn sử dụng', lSupport: 'Link Hỗ trợ',
          lWebsite: 'Website', lHotline: 'Hotline', lContactEmail: 'Email liên hệ', lPricing: 'Link bảng giá dịch vụ', appName: 'Tên app (PWA)',
          secEmail: 'Email thương hiệu', emailDesc: 'Thương hiệu cho email hệ thống/thông báo gửi tới khách của đối tác (đặt lại mật khẩu, OTP, hoá đơn...): đổi tên người gửi, logo, footer, tên/địa chỉ công ty. Địa chỉ GỬI vẫn giữ địa chỉ Callio đã xác thực để email vào được inbox.',
          eFromName: 'Tên người gửi', eFromAddress: 'Email người gửi của bạn', eLogoUrl: 'URL logo email', eCompanyName: 'Tên công ty',
          eCompanyAddress: 'Địa chỉ công ty', eFooter: 'Footer email (HTML)', eFooterPh: 'HTML footer cho email...',
          senderTitle: 'Gửi thật từ địa chỉ của bạn (Sender Signature)',
          senderGuide: 'Để email hệ thống gửi THẬT từ địa chỉ đối tác:\n1. Nhập email người gửi vào ô ngay trên (TÊN MIỀN RIÊNG, vd hotro@congty.vn — KHÔNG dùng Gmail/Yahoo/Hotmail, Postmark sẽ từ chối) → bấm Lưu.\n2. Bấm "Gửi xác nhận":\n  • Nếu tên miền đã được xác thực sẵn → chuyển "Đã xác nhận" NGAY, không cần làm gì thêm.\n  • Nếu tên miền mới → Postmark gửi 1 email tới địa chỉ đó; mở mail bấm link xác nhận rồi bấm "Kiểm tra".\n(Để trống / chưa xác nhận → vẫn gửi từ địa chỉ Callio, không gián đoạn.)',
          senderSend: 'Gửi xác nhận', senderCheck: 'Kiểm tra', senderResend: 'Gửi lại xác nhận',
          senderSaveFirst: 'Lưu domain đối tác trước để quản lý địa chỉ gửi.',
          sendMode: 'Cách gửi email hệ thống',
          modeCallio: 'Địa chỉ Callio (mặc định) — gửi từ địa chỉ Callio đã xác thực, mang branding đối tác. Không cần cấu hình.',
          modeSignature: 'Địa chỉ đối tác qua Postmark (Sender Signature) — cần tên miền riêng + DNS.',
          modeSmtp: 'Mail server riêng của đối tác (SMTP) — gửi qua chính server của đối tác.',
          modeCallioHint: 'Email gửi từ địa chỉ Callio nhưng mang branding đối tác (logo/footer/tên người gửi). Deliverability tốt nhất, không cần cấu hình gì.',
          smtpTitle: 'Mail server riêng của đối tác (SMTP)',
          smtpHost: 'Máy chủ SMTP', smtpPort: 'Cổng', smtpSecure: 'Mã hoá', smtpUser: 'Tài khoản (email)', smtpPass: 'Mật khẩu',
          smtpPassSet: '•••••• (đã lưu — để trống nếu không đổi)',
          smtpGuide: 'Nhập thông tin mail server của đối tác → Lưu → bấm "Gửi test".\nCổng 465 = tích SSL; cổng 587/25 = bỏ tích SSL (STARTTLS/thường).\nNếu SMTP lỗi lúc gửi, hệ thống tự fallback về địa chỉ Callio để email bảo mật không bị mất.',
          smtpTest: 'Gửi test', smtpTestTo: 'Email nhận test (mặc định: bạn)',
          smtpTestSent: 'Đã gửi email test tới {0}. Kiểm tra hộp thư.',
          secLivechat: 'Livechat', livechatCode: 'Code nhúng (web)', livechatDesc: 'Nhúng cả bộ widget chat (HTML/JS). Nếu để trống domain này sẽ KHÔNG có livechat.',
          livechatAppUrl: 'Link livechat (app mobile)', livechatAppUrlDesc: 'Link app mobile mở để chat hỗ trợ. Để trống → app không có livechat.',
          livechatPh: '<scr' + 'ipt>...</scr' + 'ipt>',
          secShare: 'Chia sẻ dữ liệu khách', shareLabel: 'Chia sẻ khách cho Callio',
          shareDesc: 'Bỏ tích: sales Callio KHÔNG thấy khách của domain này + KHÔNG đẩy lên CRM nội bộ Callio.',
          secApp: 'App mobile', appDesc: 'Link cài đặt app trên chợ (store app white-label) của đối tác. Nếu để trống sẽ ẩn lời mời tải app trên domain này.',
          appIos: 'Link App Store (iOS)', appAndroid: 'Link Google Play (Android)',
          appIosVersion: 'Version mới nhất iOS', appAndroidVersion: 'Version mới nhất Android',
          secPayment: 'Thanh toán', paymentEnabled: 'Dùng tài khoản ngân hàng riêng của đối tác',
          paymentDesc: 'Khi bật, trang thanh toán hiện tài khoản ngân hàng của đối tác + tự sinh mã QR VietQR. Tiền vào tài khoản đối tác — Callio KHÔNG tự đối soát/nạp credit.',
          paymentBank: 'Ngân hàng', paymentChooseBank: 'Chọn ngân hàng', paymentHolder: 'Chủ tài khoản', paymentNumber: 'Số tài khoản'
        }
      }
    }
  },
  methods: {
    // Bắt buộc chọn Tài khoản đối tác trước khi lưu (giữ modal mở nếu thiếu)
    onSave: function (evt) {
      // Chỉ owner/admin mới quản lý field "Tài khoản đối tác" → mới cần check bắt buộc.
      if (this.canEditMeta && !this.formModel.agency) {
        if (evt) evt.preventDefault();
        this.$swal({ text: this.$t('partnerDomain.agencyRequired'), icon: 'error' });
        return;
      }
      this.save(evt); // save() của mixin CRUD
    },
    // Chọn ngân hàng → lưu kèm tên hiển thị (Billing dùng để hiện, không cần danh sách bank)
    onBankChange: function () {
      const b = this.banks.find(x => x.code === this.formModel.payment.bankCode);
      this.formModel.payment.bankName = b ? b.name : '';
    },
    senderApi: function (method, action, okMsg) {
      this.senderActing = true;
      this.$service({ method, url: `/partner-domain/${this.formModel.id}/sender/${action}` }).then(res => {
        if (res.data && res.data.senderStatus) this.$set(this.formModel.email, 'senderStatus', res.data.senderStatus);
        this.$swal({ text: okMsg, icon: 'success', toast: true, position: 'top-end', timer: 3000, showConfirmButton: false });
      }).catch(error => {
        const msg = error.response && error.response.data && (error.response.data.message || error.response.data.msg);
        this.$swal({ text: msg || 'Có lỗi xảy ra', icon: 'error' });
      }).finally(() => { this.senderActing = false; });
    },
    senderConfirm: function () { this.senderApi('post', 'confirm', 'Đã gửi email xác nhận tới địa chỉ này. Mở hộp thư và bấm link xác nhận.'); },
    senderCheck: function () { this.senderApi('get', 'status', 'Đã cập nhật trạng thái.'); },
    senderResend: function () { this.senderApi('post', 'resend', 'Đã gửi lại email xác nhận.'); },
    // Test SMTP đối tác — gửi email thật qua server đối tác đã LƯU (phải Lưu trước)
    smtpTest: function () {
      this.smtpTesting = true; this.smtpTestMsg = '';
      this.$service.post(`/partner-domain/${this.formModel.id}/email/smtp-test`, { testTo: this.smtpTestTo || undefined }).then(res => {
        this.smtpTestOk = true;
        this.smtpTestMsg = this.$t('partnerDomain.smtpTestSent', [(res.data && res.data.to) || '']);
      }).catch(error => {
        this.smtpTestOk = false;
        this.smtpTestMsg = (error.response && error.response.data && (error.response.data.msg || error.response.data.message)) || 'Có lỗi xảy ra';
      }).finally(() => { this.smtpTesting = false; });
    },
    // Trả danh sách màu đã set (tránh v-for+v-if trên cùng element)
    activeColors: function (item) {
      if (!item || !item.branding) return [];
      return this.colorFields.filter(c => item.branding[c]);
    },
    load: function () {
      let loader = this.$loading.show();
      this.listLoading = true;
      this.$service.get(this.service, { params: this.filterFormModel }).then(response => {
        this.apply(response.data);
      }).catch(error => {
        if (error.response && error.response.data && error.response.data.message) {
          this.$swal({ text: error.response.data.message, icon: 'error' });
        }
      }).finally(() => {
        loader.hide();
        this.listLoading = false;
      });
    },
    create: function () {
      // Override để branding luôn là object MỚI (tránh share reference giữa các lần tạo)
      this.formModel = {
        domain: null,
        name: null,
        agency: null,
        client: null,
        enabled: true,
        appName: '',
        shareCustomersWithCallio: true,
        branding: emptyBranding(),
        links: emptyLinks(),
        email: emptyEmail(),
        livechat: { embedCode: '', appUrl: '' },
        app: emptyApp(),
        payment: emptyPayment()
      };
      this.formTitle = this.$t('create');
      this.formErrors = [];
      this.showForm = true;
    },
    update: function (id) {
      let loader = this.$loading.show();
      this.$service.get(this.service + '/' + id).then(response => {
        let data = response.data || {};
        // Đảm bảo các object tồn tại đủ field cho data cũ
        data.branding = Object.assign(emptyBranding(), data.branding || {});
        data.links = Object.assign(emptyLinks(), data.links || {});
        data.email = Object.assign(emptyEmail(), data.email || {});
        data.livechat = Object.assign({ embedCode: '', appUrl: '' }, data.livechat || {});
        data.app = Object.assign(emptyApp(), data.app || {});
        data.payment = Object.assign(emptyPayment(), data.payment || {});
        if (data.appName === undefined) data.appName = '';
        if (data.shareCustomersWithCallio === undefined) data.shareCustomersWithCallio = true;
        this.formModel = data;
        this.formModel.id = id;
        this.formTitle = this.$t('update');
        this.formErrors = [];
        this.showForm = true;
      }).finally(() => { loader.hide(); });
    },
    uploadAsset: function (file, field, maxMb) {
      if (!file) return;
      maxMb = maxMb || 2;
      if (file.size / (1024 * 1024) > maxMb) {
        this.$swal({ text: this.$t('partnerDomain.choosePngJpg') + ' (≤ ' + maxMb + 'MB)', icon: 'error' });
        return;
      }
      let loader = this.$loading.show();
      const formData = new FormData();
      formData.append('file', file);
      this.$service.post('/upload/upload-image', formData).then(response => {
        const arr = response.data;
        if (Array.isArray(arr) && arr.length && arr[0].url) {
          this.$set(this.formModel.branding, field, arr[0].url);
        }
      }).catch(error => {
        const msg = error.response && error.response.data && error.response.data.message;
        this.$swal({ text: msg || 'Upload failed', icon: 'error' });
      }).finally(() => { loader.hide(); });
    }
  }
};
</script>

<style scoped>
.pd-form .pd-section {
  border: 1px solid #ececec;
  border-radius: 8px;
  padding: 16px 16px 4px;
  margin-bottom: 18px;
  background: #fcfcfd;
}
.pd-form .pd-section__title {
  font-weight: 700;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: .04em;
  color: #6c5ce7;
  margin: -4px 0 14px;
  padding-bottom: 8px;
  border-bottom: 1px solid #eee;
}
.pd-form .pd-section__title svg { margin-right: 6px; }
.pd-agency-filter >>> .form-group { margin-bottom: 0; }
</style>
